'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonItem {
  id: string;
  title: string;
  icon: string;
  features: {
    tolerance: string;
    materials: string;
    complexity: string;
    volume: string;
    leadTime: string;
    cost: string;
  };
}

interface ComparisonMatrixProps {
  capabilities: ComparisonItem[];
}

const ComparisonMatrix = ({ capabilities }: ComparisonMatrixProps) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleSelection = (id: string) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(item => item !== id));
    } else if (selectedItems.length < 3) {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const selectedCapabilities = capabilities.filter(cap => selectedItems.includes(cap.id));

  const featureLabels = {
    tolerance: 'Tolerance Range',
    materials: 'Material Options',
    complexity: 'Complexity Level',
    volume: 'Production Volume',
    leadTime: 'Typical Lead Time',
    cost: 'Cost Range'
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-headline text-2xl font-bold text-foreground mb-1">
            Capability Comparison
          </h2>
          <p className="font-body text-sm text-technical-gray">
            Select up to 3 capabilities to compare side-by-side
          </p>
        </div>
        {selectedItems.length > 0 && (
          <button
            onClick={clearSelection}
            className="px-4 py-2 bg-muted text-accent font-cta text-xs rounded-lg hover:bg-accent/10 transition-all duration-300"
          >
            CLEAR SELECTION
          </button>
        )}
      </div>

      {/* Selection Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {capabilities.map((capability) => {
          const isSelected = selectedItems.includes(capability.id);
          const isDisabled = !isSelected && selectedItems.length >= 3;
          
          return (
            <button
              key={capability.id}
              onClick={() => !isDisabled && toggleSelection(capability.id)}
              disabled={isDisabled}
              className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                isSelected
                  ? 'border-accent bg-accent/10'
                  : isDisabled
                  ? 'border-border bg-muted/50 opacity-50 cursor-not-allowed' :'border-border hover:border-accent hover:bg-muted'
              }`}
            >
              <Icon
                name={capability.icon as any}
                size={32}
                className={`mx-auto mb-2 ${isSelected ? 'text-accent' : 'text-technical-gray'}`}
              />
              <span className={`font-cta text-xs block ${isSelected ? 'text-accent' : 'text-foreground'}`}>
                {capability.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* Comparison Table */}
      {selectedCapabilities.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-4 px-4 font-headline text-sm font-semibold text-foreground">
                  Feature
                </th>
                {selectedCapabilities.map((capability) => (
                  <th
                    key={capability.id}
                    className="text-center py-4 px-4 font-headline text-sm font-semibold text-foreground"
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <Icon name={capability.icon as any} size={24} className="text-accent" />
                      <span>{capability.title}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(featureLabels).map(([key, label]) => (
                <tr key={key} className="border-b border-border/50 hover:bg-muted/30 transition-colors duration-300">
                  <td className="py-4 px-4 font-body text-sm text-technical-gray">
                    {label}
                  </td>
                  {selectedCapabilities.map((capability) => (
                    <td
                      key={capability.id}
                      className="py-4 px-4 text-center font-body text-sm text-foreground"
                    >
                      {capability.features[key as keyof typeof capability.features]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {selectedCapabilities.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Squares2X2Icon" size={48} className="text-technical-gray mx-auto mb-4 opacity-50" />
          <p className="font-body text-sm text-technical-gray">
            Select capabilities above to begin comparison
          </p>
        </div>
      )}

      {/* Expand Details */}
      {selectedCapabilities.length > 0 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-6 flex items-center justify-center space-x-2 text-accent hover:text-accent/80 transition-colors duration-300"
        >
          <span className="font-cta text-xs">
            {isExpanded ? 'HIDE DETAILED COMPARISON' : 'SHOW DETAILED COMPARISON'}
          </span>
          <Icon
            name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            size={16}
          />
        </button>
      )}

      {/* Expanded Details */}
      {isExpanded && selectedCapabilities.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border space-y-4 animate-slide-in-left">
          <h3 className="font-headline text-lg font-semibold text-foreground mb-4">
            Detailed Analysis
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedCapabilities.map((capability) => (
              <div key={capability.id} className="bg-muted rounded-lg p-4 border border-border">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name={capability.icon as any} size={20} className="text-accent" />
                  <h4 className="font-headline text-sm font-semibold text-foreground">
                    {capability.title}
                  </h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="font-body text-xs text-technical-gray">
                      AS9100 Certified
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="font-body text-xs text-technical-gray">
                      ITAR Compliant
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="font-body text-xs text-technical-gray">
                      Full Traceability
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonMatrix;